const _ = require("lodash");

/**
 * Cache of key-value pairs using the least-recently-used algorithm. size is
 * the number of items to maintain. keys can be anything since this uses an
 * ES6 map internally.
 *
 * ---
 *
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const LRUCache = function(size) {
	this.size = size || 100;
	this.data = new Map();
	this.recent = [];
	this.onDestroyFuncs = [];
};

// Internal helper, marking the key as the last to be used.
LRUCache.prototype._setMostRecent = function(key) {
	if (this.recent.length && this.recent[0] === key) {
		return;
	}
	this.recent = _.without(this.recent, key);
	this.recent.unshift(key);
	this.limit(this.size);
};

// Store the given key-value in the cache and set it as just used.
LRUCache.prototype.store = function(key, val) {
	this.data.set(key, val);
	this._setMostRecent(key);
};

// Returns whether the key is in the cache.
LRUCache.prototype.has = function(key) {
	return this.data.has(key);
};

// Retrieve the value for a key. Optionally provide a function which takes the
// key as a parameter and returns the computed value, which will compute and
// store the value in the cache if it is not already there. Returns undefined
// if the key is not cached and no function is provided.
LRUCache.prototype.get = function(key, computeValue) {
	if (this.data.has(key)) {
		this._setMostRecent(key);
		return this.data.get(key);
	} else if (typeof computeValue === "function") {
		const val = computeValue(key);
		this.store(key, val);
		return val;
	}
};

LRUCache.prototype.remove = function(key) {
	if (this.data.has(key)) {
		const val = this.data.get(key);
		this.onDestroyFuncs.forEach(f => f(val, key));
		this.data.delete(key);
	}
	// We keep the entry in this.recent. It will eventually slide off.
};

LRUCache.prototype.removeAll = function() {
	this.data.forEach((val, key) => {
		this.onDestroyFuncs.forEach(f => f(val, key));
	});
	this.data.clear();
	this.recent = [];
};

LRUCache.prototype.onDestroy = function(f) {
	this.onDestroyFuncs.push(f);
};

// limits cache to the first N most recent elements
LRUCache.prototype.limit = function(n) {
	if (this.recent.length > n) {
		for (let i = n; i < this.recent.length; i += 1) {
			this.remove(this.recent[i]);
		}
		this.recent = this.recent.slice(0, n);
	}
};

module.exports = LRUCache;
